import React, { useState, useEffect } from 'react';
import './UserDetailsPanel.css';

function UserDetailsPanel({ selectedUser, onClose, socket }) {
  const [activeTab, setActiveTab] = useState('general');
  const [userData, setUserData] = useState(null);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState(null);

  // חיפוש
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('phone'); // 'phone' or 'email'

  // מודל גישה לפלטפורמה
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessReason, setAccessReason] = useState('');
  const [grantingAccess, setGrantingAccess] = useState(false);

  // מודל שליחת הזמנה
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitationData, setInvitationData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: selectedUser?.phone || selectedUser?.phoneNumber || '',
    courseType: 'combined'
  });

  // מודל אישור שינוי שלב
  const [showStageConfirmModal, setShowStageConfirmModal] = useState(false);
  const [pendingStageChange, setPendingStageChange] = useState(null);

  // תגיות ושלבי התקדמות
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [progressStage, setProgressStage] = useState('watching_training');
  const [showTagInput, setShowTagInput] = useState(false);


  // יומני מסחר
  const [journals, setJournals] = useState([]);
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [journalTrades, setJournalTrades] = useState([]);
  const [loadingJournals, setLoadingJournals] = useState(false);
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [loadingTrades, setLoadingTrades] = useState(false);

  // תרגולים ומשימות
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [userPhases, setUserPhases] = useState([]);
  const [loadingExercises, setLoadingExercises] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState(1);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  // מודל נעילת/פתיחת שלב
  const [showPhaseLockModal, setShowPhaseLockModal] = useState(false);
  const [pendingPhaseLock, setPendingPhaseLock] = useState(null);

  // שלבי ההתקדמות
  const progressStages = [
    { id: 'watching_training', label: 'צפייה בהכשרה', icon: '📺', color: '#4A90E2' },
    { id: 'exercises', label: 'תרגולים', icon: '✍️', color: '#F5A623' },
    { id: 'back_test', label: 'בק טסט', icon: '📊', color: '#7B68EE' },
    { id: 'forward', label: 'פורוורד', icon: '⏩', color: '#50C878' },
    { id: 'live_trading', label: 'מסחר בפועל', icon: '💹', color: '#00A884' },
    { id: 'completed', label: 'סיים', icon: '✅', color: '#34C759' }
  ];

  useEffect(() => {
    if (!selectedUser || !socket) {
      setLoading(false);
      return;
    }

    loadUserData();

    // טעינת תגיות ושלבי התקדמות מהצ'אט
    if (selectedUser.tags) {
      const tagsArray = selectedUser.tags.split(',').filter(t => t.trim());
      setTags(tagsArray);
    }
    if (selectedUser.progressStage) {
      setProgressStage(selectedUser.progressStage);
    }
  }, [selectedUser, socket]);

  // טעינת יומני מסחר כשנכנסים לטאב
  useEffect(() => {
    if (activeTab === 'journals' && userData && userData.userId && socket) {
      loadJournals();
    }
  }, [activeTab, userData, socket]);

  // טעינת הגשות כשנכנסים לטאב תרגולים
  useEffect(() => {
    if (activeTab === 'exercises' && userData && userData.userId && socket) {
      loadUserSubmissions();
    }
  }, [activeTab, userData, socket]);

  const loadUserData = () => {
    setLoading(true);

    // חילוץ מספר טלפון מהצ'אט
    let phoneNumber = selectedUser.phone || selectedUser.phoneNumber;

    // אם אין מספר טלפון, ננסה לחלץ מה-ID
    if (!phoneNumber && selectedUser.id) {
      phoneNumber = selectedUser.id.split('@')[0];
    }

    console.log('🔍 מחפש משתמש לפי מספר טלפון:', phoneNumber);

    // בקשת נתוני משתמש מהשרת
    socket.emit('get_user_by_phone', phoneNumber);

    // האזנה לנתונים
    const handleUserData = (data) => {
      console.log('📥 קיבלתי נתונים:', data);
      if (data.user) {
        setUserData(data.user);
        setExams(data.exams || []);
      } else {
        setUserData(null);
        setExams([]);
      }
      setLoading(false);
    };

    socket.on('user_data', handleUserData);

    return () => {
      socket.off('user_data', handleUserData);
    };
  };

  // טעינת יומני מסחר מ-Firebase
  const loadJournals = () => {
    if (!userData || !userData.userId || !socket) return;

    setLoadingJournals(true);
    console.log(`🔍 מבקש יומני מסחר עבור משתמש: ${userData.userId}`);

    socket.emit('get_user_journals', { userId: userData.userId });

    socket.once('user_journals_data', (data) => {
      console.log('📥 קיבלתי יומני מסחר:', data);
      if (data.success) {
        setJournals(data.journals || []);
      } else {
        console.error('שגיאה בטעינת יומני מסחר:', data.error);
        setJournals([]);
      }
      setLoadingJournals(false);
    });
  };

  // טעינת עסקאות של יומן ספציפי ופתיחת המודל
  const loadJournalTrades = (journal) => {
    if (!userData || !userData.userId || !socket) return;

    setSelectedJournal(journal);
    setShowJournalModal(true);
    setLoadingTrades(true);

    console.log(`🔍 מבקש עסקאות עבור יומן: ${journal.id}`);

    socket.emit('get_journal_trades', {
      userId: userData.userId,
      journalId: journal.id
    });

    socket.once('journal_trades_data', (data) => {
      console.log('📥 קיבלתי עסקאות:', data);
      if (data.success) {
        setJournalTrades(data.trades || []);
      } else {
        console.error('שגיאה בטעינת עסקאות:', data.error);
        setJournalTrades([]);
      }
      setLoadingTrades(false);
    });
  };

  // סגירת מודל היומן
  const closeJournalModal = () => {
    setShowJournalModal(false);
    setSelectedJournal(null);
    setJournalTrades([]);
  };

  // טעינת הגשות המשתמש מ-Firebase
  const loadUserSubmissions = () => {
    if (!userData || !userData.userId || !socket) return;

    setLoadingExercises(true);

    socket.emit('get_user_submissions', {
      userId: userData.userId
    });

    socket.once('user_submissions_loaded', (data) => {
      if (data.success) {
        setUserSubmissions(data.submissions || []);
        setUserPhases(data.phases || []);
        console.log('✅ הגשות נטענו:', data.submissions.length);
      } else {
        console.error('❌ שגיאה בטעינת הגשות:', data.error);
        setUserSubmissions([]);
        setUserPhases([]);
      }
      setLoadingExercises(false);
    });
  };

  // נעילה/פתיחה של שלב תרגולים - פתיחת מודל
  const handleTogglePhaseLock = (phaseId, currentlyUnlocked, phaseData) => {
    if (!userData || !userData.userId || !socket) return;

    setPendingPhaseLock({
      phaseId,
      currentlyUnlocked,
      phaseData,
      action: currentlyUnlocked ? 'lock' : 'unlock'
    });
    setShowPhaseLockModal(true);
  };

  // אישור נעילה/פתיחה של שלב
  const confirmPhaseLockToggle = () => {
    if (!pendingPhaseLock || !userData || !socket) return;

    const { phaseId, currentlyUnlocked } = pendingPhaseLock;

    socket.emit('toggle_phase_lock', {
      userId: userData.userId,
      phase: phaseId,
      unlock: !currentlyUnlocked
    });

    socket.once('phase_lock_toggled', (data) => {
      if (data.success) {
        // עדכון ה-state המקומי - טעינה מחדש מה-Firebase
        loadUserSubmissions();

        setSyncStatus(`✅ שלב ${phaseId} ${currentlyUnlocked ? 'ננעל' : 'נפתח'} בהצלחה!`);
        setTimeout(() => setSyncStatus(null), 2000);
      } else {
        setSyncStatus(`❌ שגיאה: ${data.error}`);
        setTimeout(() => setSyncStatus(null), 3000);
      }
    });

    // סגירת המודל
    setShowPhaseLockModal(false);
    setPendingPhaseLock(null);
  };

  // ביטול נעילה/פתיחה
  const cancelPhaseLockToggle = () => {
    setShowPhaseLockModal(false);
    setPendingPhaseLock(null);
  };

  // חיפוש לפי מספר/מייל
  const handleSearch = () => {
    if (!searchQuery.trim() || !socket) return;

    setLoading(true);

    if (searchType === 'phone') {
      socket.emit('get_user_by_phone', searchQuery.trim());
    } else {
      // חיפוש לפי מייל - נוסיף endpoint חדש
      socket.emit('get_user_by_email', searchQuery.trim());
    }
  };

  // פונקציה לסנכרון ידני (רק למשתמשים רשומים)
  const handleManualSync = () => {
    if (!socket) return;
    setSyncStatus('מסנכרן...');
    socket.emit('sync_firebase');

    socket.once('sync_complete', (result) => {
      if (result.success) {
        setSyncStatus(`✅ סנכרון הושלם! ${result.updatedUsers} משתמשים עודכנו`);
        setTimeout(() => setSyncStatus(null), 3000);
        loadUserData();
      } else {
        setSyncStatus(`❌ שגיאה: ${result.error}`);
        setTimeout(() => setSyncStatus(null), 3000);
      }
    });
  };

  // איפוס סיסמה - שליחת קישור בצ'אט
  const handleResetPassword = async () => {
    if (!userData || !socket || !selectedUser) return;

    const confirmed = window.confirm('האם לשלוח קישור איפוס סיסמה ל-' + userData.fullName + ' בצ\'אט?');
    if (!confirmed) return;

    setSyncStatus('שולח קישור איפוס סיסמה...');

    // יצירת קישור איפוס סיסמה
    const resetLink = 'https://darkcrypto.io/reset-password?email=' + encodeURIComponent(userData.email);

    const message = '🔐 קישור לאיפוס סיסמה\n\n' +
      'שלום ' + userData.fullName + ',\n\n' +
      'להלן קישור לאיפוס סיסמה שלך בפלטפורמה:\n' +
      resetLink + '\n\n' +
      '🔹 הקישור בתוקף ל-24 שעות\n' +
      '🔹 לאחר איפוס הסיסמה תוכל להתחבר עם הסיסמה החדשה\n\n' +
      '💡 לכל שאלה או בעיה, אנחנו כאן לעזור!';

    // שליחת ההודעה בצ'אט
    socket.emit('send_message', {
      chatId: selectedUser.id,
      body: message
    });

    setSyncStatus('קישור איפוס סיסמה נשלח בצ\'אט!');
    setTimeout(() => setSyncStatus(null), 3000);
  };

  // מתן גישה לפלטפורמה - שליחת קישור כניסה בצ'אט
  const handleGrantAccess = async () => {
    if (!userData || !socket || !selectedUser) return;

    const confirmed = window.confirm('האם לשלוח קישור גישה לפלטפורמה ל-' + userData.fullName + ' בצ\'אט?');
    if (!confirmed) return;

    setGrantingAccess(true);
    setSyncStatus('שולח קישור גישה לפלטפורמה...');

    // יצירת קישור כניסה לפלטפורמה
    const loginLink = 'https://darkcrypto.io/login';

    const message = '🎓 גישה לפלטפורמת Dark Crypto\n\n' +
      'שלום ' + userData.fullName + ',\n\n' +
      'ניתנה לך גישה לפלטפורמה! 🎉\n\n' +
      '🔗 קישור לכניסה לפלטפורמה:\n' +
      loginLink + '\n\n' +
      '📧 התחבר עם האימייל שלך: ' + userData.email + '\n' +
      '🔐 השתמש בסיסמה שקיבלת בהרשמה\n\n' +
      '💡 לכל שאלה או בעיה בהתחברות, אנחנו כאן לעזור!\n\n' +
      'בהצלחה! 🚀';

    // שליחת ההודעה בצ'אט
    socket.emit('send_message', {
      chatId: selectedUser.id,
      body: message
    });

    // עדכון ב-Firebase שניתנה גישה (ללא סיבה)
    socket.emit('grant_platform_access', {
      userId: userData.userId,
      grantedBy: 'מנטור',
      grantedAt: Date.now()
    });

    socket.once('access_granted_result', (result) => {
      setGrantingAccess(false);
      if (result.success) {
        setSyncStatus('קישור גישה נשלח בצ\'אט!');
        setShowAccessModal(false);
        setAccessReason('');
        loadUserData();
      } else {
        setSyncStatus('שגיאה: ' + result.error);
      }
      setTimeout(() => setSyncStatus(null), 3000);
    });
  };

  // הוספת תגית
  const handleAddTag = () => {
    if (!newTag.trim() || !socket || !selectedUser) return;

    const updatedTags = [...tags, newTag.trim()];
    const tagToAdd = newTag.trim();
    setNewTag('');
    setShowTagInput(false);

    // עדכון מיידי ב-UI
    setTags(updatedTags);

    socket.emit('update_contact_tags', {
      chatId: selectedUser.id,
      tags: updatedTags
    });

    socket.once('tags_update_result', (result) => {
      if (!result.success) {
        alert(`שגיאה בהוספת תגית: ${result.error}`);
        setTags(tags); // חזרה למצב הקודם
      } else {
        console.log('✅ תגית נוספה בהצלחה:', tagToAdd);
      }
    });
  };

  // הסרת תגית
  const handleRemoveTag = (tagToRemove) => {
    if (!socket || !selectedUser) return;

    const updatedTags = tags.filter(tag => tag !== tagToRemove);

    // עדכון מיידי ב-UI
    setTags(updatedTags);

    socket.emit('update_contact_tags', {
      chatId: selectedUser.id,
      tags: updatedTags
    });

    socket.once('tags_update_result', (result) => {
      if (!result.success) {
        alert(`שגיאה בהסרת תגית: ${result.error}`);
        setTags(tags); // חזרה למצב הקודם
      } else {
        console.log('✅ תגית הוסרה בהצלחה:', tagToRemove);
      }
    });
  };

  // פתיחת מודל אישור שינוי שלב
  const handleProgressStageChange = (newStage) => {
    if (!socket || !selectedUser || !userData) return;

    // שמירת השלב החדש ופתיחת המודל
    setPendingStageChange(newStage);
    setShowStageConfirmModal(true);
  };

  // אישור שינוי שלב
  const confirmStageChange = () => {
    if (!pendingStageChange) return;

    const newStage = pendingStageChange;
    const previousStage = progressStage;

    // סגירת המודל
    setShowStageConfirmModal(false);
    setPendingStageChange(null);

    // עדכון מיידי ב-UI
    setProgressStage(newStage);
    setSyncStatus('מעדכן שלב התקדמות...');

    // שליחה לשרת - השרת ידאג לעדכן גם ב-Firebase וגם ב-SQLite
    socket.emit('update_progress_stage', {
      chatId: selectedUser.id,
      userId: userData.userId,
      stage: newStage,
      userName: userData.fullName || selectedUser.name
    });

    socket.once('progress_update_result', (result) => {
      if (!result.success) {
        alert(`שגיאה בעדכון שלב התקדמות: ${result.error}`);
        setProgressStage(previousStage);
        setSyncStatus(null);
      } else {
        console.log('✅ שלב התקדמות עודכן ב-Firebase וב-SQLite:', newStage);
        setSyncStatus('✅ שלב ההתקדמות עודכן בהצלחה!');
        setTimeout(() => setSyncStatus(null), 2000);
      }
    });
  };

  // ביטול שינוי שלב
  const cancelStageChange = () => {
    setShowStageConfirmModal(false);
    setPendingStageChange(null);
  };

  // שליחת הזמנה
  const handleSendInvitation = () => {
    if (!invitationData.firstName || !invitationData.lastName) {
      alert('נא למלא שם פרטי ושם משפחה');
      return;
    }

    if (!invitationData.email && !invitationData.phone) {
      alert('נא למלא מייל או טלפון');
      return;
    }

    socket.emit('create_invitation', invitationData);

    socket.once('invitation_created', (result) => {
      if (result.success) {
        setSyncStatus('✅ ההזמנה נשלחה בהצלחה!');
        setShowInvitationModal(false);

        // שליחת ההודעה ב-WhatsApp
        if (invitationData.phone) {
          const message = `🎓 ברוכים הבאים לפלטפורמה של דארק קריפטו!

📚 קישור להרשמה:
${result.registrationUrl}

🌐 קישור לפלטפורמה:
https://darkcrypto.io/

💡 מומלץ לבצע את ההרשמה ואת השימוש דרך המחשב לחוויה מיטבית.

📱 לכל שאלה או בעיה, אנחנו כאן לעזור!

בהצלחה! 🚀`;

          socket.emit('send_message', {
            chatId: selectedUser.id,
            body: message
          });
        }

        setTimeout(() => setSyncStatus(null), 3000);
      } else {
        alert(`שגיאה: ${result.error}`);
      }
    });
  };

  // קביעת סוג ההכשרה (Combined עוקף הכל)
  const getTrainingType = () => {
    if (!userData) return 'לא ידוע';

    // אם combined = true, זה רק המשולבת
    if (userData.permission_combined) {
      return 'המשולבת';
    }

    // אחרת, תצוגה לפי מה שמסומן
    const types = [];
    if (userData.permission_premium) types.push('פרימיום');
    if (userData.permission_explosive_coins) types.push('מטבעות מתפוצצים');

    return types.length > 0 ? types.join(' + ') : 'לא ידוע';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'לא ידוע';
    const date = new Date(timestamp);
    return date.toLocaleDateString('he-IL');
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'לא ידוע';
    const date = new Date(timestamp);
    return date.toLocaleString('he-IL');
  };

  if (!selectedUser) {
    return (
      <div className="user-details-panel empty">
        <div className="empty-state">
          <svg viewBox="0 0 24 24" width="64" height="64">
            <path fill="#8696a0" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
          <p>בחר איש קשר כדי לראות את הפרטים</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-details-panel">
      <div className="panel-header">
        <div className="user-info-header">
          <div className="user-avatar-large">
            {selectedUser.profilePic ? (
              <img src={selectedUser.profilePic} alt={selectedUser.name} />
            ) : (
              <div className="avatar-placeholder">
                {selectedUser.name?.charAt(0) || 'U'}
              </div>
            )}
          </div>
          <div className="user-main-info">
            <h2>{selectedUser.name || 'לא ידוע'}</h2>
            <p className="user-phone">{selectedUser.phone || selectedUser.phoneNumber || 'לא ידוע'}</p>

            {/* תגיות */}
            <div className="user-tags">
              {tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                  <button onClick={() => handleRemoveTag(tag)} className="tag-remove">×</button>
                </span>
              ))}
              {showTagInput ? (
                <div className="tag-input-container">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    placeholder="הזן תגית..."
                    autoFocus
                  />
                  <button onClick={handleAddTag} className="tag-add-btn">✓</button>
                  <button onClick={() => { setShowTagInput(false); setNewTag(''); }} className="tag-cancel-btn">×</button>
                </div>
              ) : (
                <button onClick={() => setShowTagInput(true)} className="tag-add-trigger">+ הוסף תגית</button>
              )}
            </div>
          </div>
        </div>
        <button className="close-panel-btn" onClick={onClose} title="סגור">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M19.6 17.8L13.3 11.5l6.3-6.3c.5-.5.5-1.3 0-1.8-.5-.5-1.3-.5-1.8 0l-6.3 6.3-6.3-6.3c-.5-.5-1.3-.5-1.8 0-.5.5-.5 1.3 0 1.8l6.3 6.3-6.3 6.3c-.5.5-.5 1.3 0 1.8.3.3.6.4.9.4s.6-.1.9-.4l6.3-6.3 6.3 6.3c.3.3.6.4.9.4s.6-.1.9-.4c.5-.5.5-1.3 0-1.8z"/>
          </svg>
        </button>
      </div>

      {syncStatus && (
        <div className="sync-status-banner">
          {syncStatus}
        </div>
      )}

      {/* חיפוש */}
      <div className="search-section">
        <div className="search-controls">
          <select value={searchType} onChange={(e) => setSearchType(e.target.value)}>
            <option value="phone">מספר טלפון</option>
            <option value="email">אימייל</option>
          </select>
          <input
            type="text"
            placeholder={searchType === 'phone' ? 'הזן מספר טלפון...' : 'הזן אימייל...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch} className="search-btn">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="currentColor" d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            חפש
          </button>
        </div>
      </div>

      {/* סקציית שלב התקדמות מעל הטאבים - רק אם המשתמש קיים */}
      {userData && (
        <div className="progress-stage-header">
          <div className="current-stage-display">
            <span className="stage-icon-small">
              {progressStages.find(s => s.id === progressStage)?.icon || '📺'}
            </span>
            <div className="stage-info">
              <span className="stage-label-small">שלב נוכחי</span>
              <span className="stage-name">{progressStages.find(s => s.id === progressStage)?.label || 'צפייה בהכשרה'}</span>
            </div>
          </div>
        </div>
      )}

      <div className="panel-tabs">
        <button
          className={`tab-btn ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
          </svg>
          פרטים
        </button>
        <button
          className={`tab-btn ${activeTab === 'progress' ? 'active' : ''}`}
          onClick={() => setActiveTab('progress')}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="currentColor" d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
          </svg>
          התקדמות
        </button>
        <button
          className={`tab-btn ${activeTab === 'exercises' ? 'active' : ''}`}
          onClick={() => setActiveTab('exercises')}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="currentColor" d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
          </svg>
          תרגולים
        </button>
        <button
          className={`tab-btn ${activeTab === 'journals' ? 'active' : ''}`}
          onClick={() => setActiveTab('journals')}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="currentColor" d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
          </svg>
          יומני מסחר
        </button>
      </div>

      <div className="panel-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>טוען נתונים...</p>
          </div>
        ) : userData ? (
          <>
            {/* טאב פרטים כלליים */}
            {activeTab === 'general' && (
              <div className="tab-content general-tab">
                {/* כפתורי פעולה */}
                <section className="action-buttons">
                  <button className="action-btn reset-password" onClick={handleResetPassword}>
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path fill="currentColor" d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                    </svg>
                    איפוס סיסמה
                  </button>
                  <button className="action-btn platform-access" onClick={() => setShowAccessModal(true)}>
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    גישה לפלטפורמה
                  </button>
                </section>

                <section className="info-section">
                  <h3>פרטי משתמש</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>שם מלא</label>
                      <p>{userData.fullName || 'לא ידוע'}</p>
                    </div>

                    <div className="info-item">
                      <label>שם משתמש</label>
                      <p>{userData.username || 'לא הוזן'}</p>
                    </div>

                    <div className="info-item">
                      <label>אימייל</label>
                      <p>{userData.email || 'לא ידוע'}</p>
                    </div>

                    <div className="info-item">
                      <label>טלפון</label>
                      <p>{userData.phone || 'לא ידוע'}</p>
                    </div>

                    <div className="info-item">
                      <label>מגדר</label>
                      <p>{userData.gender === 'male' ? 'זכר' : userData.gender === 'female' ? 'נקבה' : 'לא הוזן'}</p>
                    </div>

                    <div className="info-item">
                      <label>תאריך לידה</label>
                      <p>{userData.birthDate || 'לא הוזן'}</p>
                    </div>

                    <div className="info-item full-width">
                      <label>סוג הכשרה</label>
                      <p className="training-type-badge">
                        {getTrainingType()}
                      </p>
                    </div>

                    <div className="info-item">
                      <label>פעילות אחרונה</label>
                      <p>{formatDateTime(userData.lastActivity)}</p>
                    </div>

                    <div className="info-item">
                      <label>התחברות אחרונה</label>
                      <p>{formatDateTime(userData.lastLogin)}</p>
                    </div>

                    <div className="info-item">
                      <label>תנאים אושרו</label>
                      <p>{userData.termsAccepted ? `✅ ${formatDate(userData.termsAcceptedAt)}` : '❌ לא'}</p>
                    </div>
                  </div>
                </section>

              </div>
            )}

            {/* טאב התקדמות */}
            {activeTab === 'progress' && (
              <div className="tab-content progress-tab">
                {/* Progress Bar עם 6 שלבים - עיצוב מחדש */}
                <section className="info-section progress-section">
                  <h3>מסלול ההתקדמות שלך</h3>
                  <p className="section-description">לחץ על כל שלב כדי לעדכן את ההתקדמות</p>

                  <div className="progress-stages-grid">
                    {progressStages.map((stage, index) => {
                      const currentIndex = progressStages.findIndex(s => s.id === progressStage);
                      const isCompleted = index <= currentIndex;
                      const isActive = stage.id === progressStage;

                      return (
                        <div
                          key={stage.id}
                          className={`progress-card ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}
                          onClick={() => handleProgressStageChange(stage.id)}
                        >
                          <div className="progress-card-icon" style={{ backgroundColor: isCompleted ? stage.color : '#2a3942' }}>
                            <span className="stage-icon-large">{stage.icon}</span>
                          </div>
                          <div className="progress-card-content">
                            <h4>{stage.label}</h4>
                            <span className="stage-number">שלב {index + 1}</span>
                          </div>
                          {isActive && <div className="active-indicator">נוכחי</div>}
                          {isCompleted && !isActive && (
                            <svg className="check-icon" viewBox="0 0 24 24" width="20" height="20">
                              <path fill="#fff" d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                            </svg>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section className="info-section">
                  <h3>התקדמות בקורס</h3>
                  <div className="progress-overview">
                    <div className="progress-circle-container">
                      <svg className="progress-circle" width="200" height="200">
                        <circle
                          className="progress-circle-bg"
                          cx="100"
                          cy="100"
                          r="90"
                          fill="none"
                          stroke="#e6e6e6"
                          strokeWidth="12"
                        />
                        <circle
                          className="progress-circle-fill"
                          cx="100"
                          cy="100"
                          r="90"
                          fill="none"
                          stroke="#00a884"
                          strokeWidth="12"
                          strokeDasharray={`${(userData.completedLessons || 0) * 5.65} 565`}
                          strokeLinecap="round"
                          transform="rotate(-90 100 100)"
                        />
                        <text x="100" y="100" textAnchor="middle" dy=".3em" fontSize="32" fill="#000">
                          {userData.completedLessons || 0}
                        </text>
                        <text x="100" y="130" textAnchor="middle" fontSize="14" fill="#8696a0">
                          שיעורים
                        </text>
                      </svg>
                    </div>
                  </div>
                </section>

                {/* מבחנים - הועבר מטאב נפרד */}
                <section className="info-section">
                  <h3>מבחנים ({exams.length})</h3>
                  {exams.length > 0 ? (
                    <div className="exams-full-list">
                      {exams.map((exam, index) => (
                        <div key={index} className="exam-full-item">
                          <div className="exam-header">
                            <h4>{exam.examId}</h4>
                            <span className={`exam-badge ${exam.passed ? 'passed' : 'failed'}`}>
                              {exam.passed ? 'עבר' : 'נכשל'}
                            </span>
                          </div>
                          <div className="exam-details">
                            <div className="exam-detail">
                              <label>ציון:</label>
                              <span className={`score-text ${exam.score >= 80 ? 'high' : exam.score >= 60 ? 'medium' : 'low'}`}>
                                {exam.score}%
                              </span>
                            </div>
                            <div className="exam-detail">
                              <label>תאריך:</label>
                              <span>{formatDateTime(exam.completedAt)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-data">
                      <svg viewBox="0 0 24 24" width="48" height="48">
                        <path fill="#8696a0" d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                      </svg>
                      <p>אין מבחנים מתועדים</p>
                    </div>
                  )}
                </section>

                {/* תרגולים */}
                <section className="info-section">
                  <h3>🎓 תרגולים והגשות ({userSubmissions.length})</h3>

                  {/* שלבי תרגולים עם שליטה */}
                  <div className="exercises-phases-section">
                    <h4>שלבי התרגולים</h4>
                    <p className="section-description">נעל/פתח גישה לשלבים עבור התלמיד</p>

                    <div className="exercises-phases-grid">
                      {[
                        { id: 1, name: 'תרגולי בסיס', icon: '📚', color: '#3B82F6' },
                        { id: 2, name: 'וויקוף', icon: '📈', color: '#10B981' },
                        { id: 3, name: 'טריגרים', icon: '🎯', color: '#F59E0B' },
                        { id: 4, name: 'בק טסט', icon: '📋', color: '#8B5CF6' },
                        { id: 5, name: 'תכנית עבודה', icon: '✅', color: '#EC4899' },
                        { id: 6, name: 'מסחר בפועל', icon: '💰', color: '#F43F5E' }
                      ].map(phase => {
                        const isUnlocked = userPhases.some(p => p.phase === phase.id && p.unlocked);
                        const submissionsCount = userSubmissions.filter(s => s.phase === phase.id).length;

                        return (
                          <div
                            key={phase.id}
                            className={`exercise-phase-card ${isUnlocked ? 'unlocked' : 'locked'} ${selectedPhase === phase.id ? 'selected' : ''}`}
                            onClick={() => setSelectedPhase(phase.id)}
                          >
                            <div className="phase-header">
                              <div className="phase-icon-small" style={{ backgroundColor: phase.color }}>
                                {phase.icon}
                              </div>
                              <div className="phase-details">
                                <div className="phase-name-small">שלב {phase.id}</div>
                                <div className="phase-title-small">{phase.name}</div>
                              </div>
                            </div>

                            <div className="phase-status">
                              <span className="submissions-count">{submissionsCount} הגשות</span>
                              <button
                                className={`lock-toggle-btn ${isUnlocked ? 'unlock' : 'lock'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTogglePhaseLock(phase.id, isUnlocked, phase);
                                }}
                                title={isUnlocked ? 'נעל שלב' : 'פתח שלב'}
                              >
                                {isUnlocked ? '🔓 פתוח' : '🔒 נעול'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* הגשות בשלב הנבחר */}
                  {selectedPhase && (
                    <div className="submissions-section">
                      <h4>הגשות בשלב {selectedPhase}</h4>

                      {loadingExercises ? (
                        <div className="loading-state">
                          <div className="spinner"></div>
                          <p>טוען הגשות...</p>
                        </div>
                      ) : userSubmissions.filter(s => s.phase === selectedPhase).length > 0 ? (
                        <div className="submissions-list">
                          {userSubmissions
                            .filter(s => s.phase === selectedPhase)
                            .map(submission => (
                              <div
                                key={submission.id}
                                className="submission-card"
                                onClick={() => {
                                  setSelectedSubmission(submission);
                                  setShowSubmissionModal(true);
                                }}
                              >
                                <div className="submission-header">
                                  <h5>{submission.taskTitle || `משימה ${submission.taskId}`}</h5>
                                  <span className={`submission-status ${submission.status}`}>
                                    {submission.status === 'accepted' ? '✅ אושר' :
                                     submission.status === 'rejected' ? '❌ נדחה' :
                                     submission.status === 'reviewed' ? '👁️ נבדק' :
                                     '⏳ בבדיקה'}
                                  </span>
                                </div>

                                <div className="submission-meta">
                                  <span>📅 {new Date(submission.submittedAt || submission.createdAt).toLocaleDateString('he-IL')}</span>
                                  {submission.symbol && <span>💱 {submission.symbol}</span>}
                                  {submission.interval && <span>⏰ {submission.interval}</span>}
                                </div>

                                {submission.mentorFeedback && (
                                  <div className="submission-feedback">
                                    <strong>משוב מנטור:</strong>
                                    <p>{submission.mentorFeedback.substring(0, 100)}...</p>
                                  </div>
                                )}

                                <button className="view-submission-btn">
                                  צפה בפרטים →
                                </button>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <div className="no-data">
                          <p>אין הגשות בשלב זה</p>
                        </div>
                      )}
                    </div>
                  )}
                </section>
              </div>
            )}

            {/* טאב תרגולים */}
            {activeTab === 'exercises' && (
              <div className="tab-content exercises-tab">
                <section className="info-section">
                  <h3>🎓 תרגולים והגשות ({userSubmissions.length})</h3>

                  {/* שלבי תרגולים עם שליטה */}
                  <div className="exercises-phases-section">
                    <h4>שלבי התרגולים</h4>
                    <p className="section-description">נעל/פתח גישה לשלבים עבור התלמיד</p>

                    <div className="exercises-phases-grid">
                      {[
                        { id: 1, name: 'תרגולי בסיס', icon: '📚', color: '#3B82F6' },
                        { id: 2, name: 'וויקוף', icon: '📈', color: '#10B981' },
                        { id: 3, name: 'טריגרים', icon: '🎯', color: '#F59E0B' },
                        { id: 4, name: 'בק טסט', icon: '📋', color: '#8B5CF6' },
                        { id: 5, name: 'תכנית עבודה', icon: '✅', color: '#EC4899' },
                        { id: 6, name: 'מסחר בפועל', icon: '💰', color: '#F43F5E' }
                      ].map(phase => {
                        const isUnlocked = userPhases.some(p => p.phase === phase.id && p.unlocked);
                        const submissionsCount = userSubmissions.filter(s => s.phase === phase.id).length;

                        return (
                          <div
                            key={phase.id}
                            className={`exercise-phase-card ${isUnlocked ? 'unlocked' : 'locked'} ${selectedPhase === phase.id ? 'selected' : ''}`}
                            onClick={() => setSelectedPhase(phase.id)}
                          >
                            <div className="phase-header">
                              <div className="phase-icon-small" style={{ backgroundColor: phase.color }}>
                                {phase.icon}
                              </div>
                              <div className="phase-details">
                                <div className="phase-name-small">שלב {phase.id}</div>
                                <div className="phase-title-small">{phase.name}</div>
                              </div>
                            </div>

                            <div className="phase-status">
                              <span className="submissions-count">{submissionsCount} הגשות</span>
                              <button
                                className={`lock-toggle-btn ${isUnlocked ? 'unlock' : 'lock'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTogglePhaseLock(phase.id, isUnlocked, phase);
                                }}
                                title={isUnlocked ? 'נעל שלב' : 'פתח שלב'}
                              >
                                {isUnlocked ? '🔓 פתוח' : '🔒 נעול'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* הגשות בשלב הנבחר */}
                  {selectedPhase && (
                    <div className="submissions-section">
                      <h4>הגשות בשלב {selectedPhase}</h4>

                      {loadingExercises ? (
                        <div className="loading-state">
                          <div className="spinner"></div>
                          <p>טוען הגשות...</p>
                        </div>
                      ) : userSubmissions.filter(s => s.phase === selectedPhase).length > 0 ? (
                        <div className="submissions-list">
                          {userSubmissions
                            .filter(s => s.phase === selectedPhase)
                            .map(submission => (
                              <div
                                key={submission.id}
                                className="submission-card"
                                onClick={() => {
                                  setSelectedSubmission(submission);
                                  setShowSubmissionModal(true);
                                }}
                              >
                                <div className="submission-header">
                                  <h5>{submission.taskTitle || `משימה ${submission.taskId}`}</h5>
                                  <span className={`submission-status ${submission.status}`}>
                                    {submission.status === 'accepted' ? '✅ אושר' :
                                     submission.status === 'rejected' ? '❌ נדחה' :
                                     submission.status === 'reviewed' ? '👁️ נבדק' :
                                     '⏳ בבדיקה'}
                                  </span>
                                </div>

                                <div className="submission-meta">
                                  <span>📅 {new Date(submission.submittedAt || submission.createdAt).toLocaleDateString('he-IL')}</span>
                                  {submission.symbol && <span>💱 {submission.symbol}</span>}
                                  {submission.interval && <span>⏰ {submission.interval}</span>}
                                </div>

                                {submission.mentorFeedback && (
                                  <div className="submission-feedback">
                                    <strong>משוב מנטור:</strong>
                                    <p>{submission.mentorFeedback.substring(0, 100)}...</p>
                                  </div>
                                )}

                                <button className="view-submission-btn">
                                  צפה בפרטים →
                                </button>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <div className="no-data">
                          <p>אין הגשות בשלב זה</p>
                        </div>
                      )}
                    </div>
                  )}
                </section>
              </div>
            )}

            {/* טאב יומני מסחר */}
            {activeTab === 'journals' && (
              <div className="tab-content journals-tab">
                {loadingJournals ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p>טוען יומני מסחר...</p>
                  </div>
                ) : journals.length > 0 ? (
                  <>
                    {/* רשימת יומנים */}
                    <section className="info-section">
                      <h3>יומני המסחר ({journals.length})</h3>
                      <div className="journals-list">
                        {journals.map((journal) => (
                          <div
                            key={journal.id}
                            className="journal-card"
                            onClick={() => loadJournalTrades(journal)}
                          >
                            <div className="journal-header-row">
                              <h4>{journal.name}</h4>
                              <span className="journal-currency">{journal.currency}</span>
                            </div>
                            <p className="journal-description">{journal.description}</p>
                            <div className="journal-stats">
                              <div className="journal-stat">
                                <label>יתרה התחלתית</label>
                                <span>{journal.initialBalance?.toLocaleString()}</span>
                              </div>
                              <div className="journal-stat">
                                <label>גודל תיק</label>
                                <span>{journal.portfolioSize?.toLocaleString()}</span>
                              </div>
                              <div className="journal-stat">
                                <label>סוג סיכון</label>
                                <span>{journal.riskType === 'fixed' ? 'קבוע' : 'אחוז'}</span>
                              </div>
                              <div className="journal-stat">
                                <label>ערך סיכון</label>
                                <span>{journal.riskValue}</span>
                              </div>
                            </div>
                            <div className="journal-dates">
                              <span>נוצר: {formatDateTime(journal.createdAt)}</span>
                              <span>עודכן: {formatDateTime(journal.updatedAt)}</span>
                            </div>
                            <div className="click-to-view">
                              לחץ לצפייה בעסקאות →
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>

                    {/* מודל עסקאות */}
                    {showJournalModal && selectedJournal && (
                      <div className="modal-overlay" onClick={closeJournalModal}>
                        <div className="modal-content journal-modal" onClick={(e) => e.stopPropagation()}>
                          <div className="modal-header">
                            <h3>📊 {selectedJournal.name}</h3>
                            <button className="close-modal-btn" onClick={closeJournalModal}>×</button>
                          </div>

                          <div className="modal-body">
                            {loadingTrades ? (
                              <div className="loading-state">
                                <div className="spinner"></div>
                                <p>טוען עסקאות...</p>
                              </div>
                            ) : journalTrades.length > 0 ? (
                              <div className="trades-table-container">
                                <table className="trades-table">
                                  <thead>
                                    <tr>
                                      <th>תאריך כניסה</th>
                                      <th>מטבע</th>
                                      <th>כיוון</th>
                                      <th>מחיר כניסה</th>
                                      <th>מחיר יציאה</th>
                                      <th>לוטים</th>
                                      <th>רווח/הפסד</th>
                                      <th>תוצאה</th>
                                      <th>סוג</th>
                                      <th>פעולות</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {journalTrades.map((trade, index) => (
                                      <React.Fragment key={index}>
                                      <tr className="trade-row">
                                        <td>{formatDateTime(trade.entryDate)}</td>
                                        <td><strong>{trade.currency}</strong></td>
                                        <td>
                                          <span className={`trade-direction ${trade.direction === 'לונג' ? 'long' : 'short'}`}>
                                            {trade.direction}
                                          </span>
                                        </td>
                                        <td>{trade.entryPrice?.toFixed(5)}</td>
                                        <td>{trade.exitPrice?.toFixed(5)}</td>
                                        <td>{trade.lots}</td>
                                        <td className={trade.profitLoss >= 0 ? 'profit-text' : 'loss-text'}>
                                          <strong>{trade.profitLoss >= 0 ? '+' : ''}{trade.profitLoss?.toFixed(2)}</strong>
                                        </td>
                                        <td>
                                          <span className={`trade-result ${trade.tradeResult === 'רווח' ? 'profit' : trade.tradeResult === 'הפסד' ? 'loss' : 'breakeven'}`}>
                                            {trade.tradeResult}
                                          </span>
                                        </td>
                                        <td>{trade.isLive ? '🔴 חי' : '🟢 דמו'}</td>
                                        <td>
                                          <button
                                            className="view-details-btn"
                                            onClick={() => {
                                              const detailsRow = document.getElementById(`trade-details-${index}`);
                                              if (detailsRow.style.display === 'none' || !detailsRow.style.display) {
                                                detailsRow.style.display = 'table-row';
                                              } else {
                                                detailsRow.style.display = 'none';
                                              }
                                            }}
                                          >
                                            פרטים
                                          </button>
                                        </td>
                                      </tr>
                                      <tr id={`trade-details-${index}`} className="trade-details-row" style={{display: 'none'}}>
                                        <td colSpan="10">
                                          <div className="trade-details-content">
                                            {/* פרטי עסקה */}
                                            <div className="details-section">
                                              <h5>📋 פרטי העסקה</h5>
                                              <div className="details-grid">
                                                <div className="detail-item">
                                                  <label>סטופ לוס</label>
                                                  <span>{trade.stopLoss?.toFixed(5)}</span>
                                                </div>
                                                <div className="detail-item">
                                                  <label>טייק פרופיט</label>
                                                  <span>{trade.takeProfit?.toFixed(5)}</span>
                                                </div>
                                                <div className="detail-item">
                                                  <label>עמלה</label>
                                                  <span>{trade.commission}</span>
                                                </div>
                                                <div className="detail-item">
                                                  <label>סכום סיכון</label>
                                                  <span>{trade.riskAmount}</span>
                                                </div>
                                                <div className="detail-item">
                                                  <label>אסטרטגיה</label>
                                                  <span>{trade.strategy}</span>
                                                </div>
                                                <div className="detail-item">
                                                  <label>תאריך יציאה</label>
                                                  <span>{formatDateTime(trade.exitDate)}</span>
                                                </div>
                                              </div>
                                              {trade.notes && (
                                                <div className="detail-item full-width">
                                                  <label>הערות</label>
                                                  <p>{trade.notes}</p>
                                                </div>
                                              )}
                                            </div>

                                            {/* ניתוח נפשי */}
                                            {trade.mentalAnalysis && (
                                              <div className="details-section">
                                                <h5>🧠 ניתוח נפשי</h5>
                                                <div className="details-grid">
                                                  <div className="detail-item">
                                                    <label>רגש לפני</label>
                                                    <span>{trade.mentalAnalysis.preTradeEmotion}</span>
                                                  </div>
                                                  <div className="detail-item">
                                                    <label>רגש אחרי</label>
                                                    <span>{trade.mentalAnalysis.postTradeEmotion}</span>
                                                  </div>
                                                  <div className="detail-item">
                                                    <label>ביטחון לפני</label>
                                                    <span>{trade.mentalAnalysis.preTradeConfidence}/10</span>
                                                  </div>
                                                  <div className="detail-item">
                                                    <label>משמעת לפני</label>
                                                    <span>{trade.mentalAnalysis.preTradeDiscipline}/10</span>
                                                  </div>
                                                  <div className="detail-item">
                                                    <label>משמעת אחרי</label>
                                                    <span>{trade.mentalAnalysis.postTradeDiscipline}/10</span>
                                                  </div>
                                                  <div className="detail-item">
                                                    <label>ריכוז לפני</label>
                                                    <span>{trade.mentalAnalysis.preTradeFocus}/10</span>
                                                  </div>
                                                  <div className="detail-item">
                                                    <label>ריכוז אחרי</label>
                                                    <span>{trade.mentalAnalysis.postTradeFocus}/10</span>
                                                  </div>
                                                  <div className="detail-item">
                                                    <label>לחץ לפני</label>
                                                    <span>{trade.mentalAnalysis.preTradeStress}/10</span>
                                                  </div>
                                                  <div className="detail-item">
                                                    <label>ניהול סיכונים</label>
                                                    <span>{trade.mentalAnalysis.postTradeRiskManagement}/10</span>
                                                  </div>
                                                  <div className="detail-item">
                                                    <label>ניהול עסקה</label>
                                                    <span>{trade.mentalAnalysis.postTradeManagement}/10</span>
                                                  </div>
                                                  <div className="detail-item">
                                                    <label>שליטה נפשית</label>
                                                    <span>{trade.mentalAnalysis.postTradeMentalControl}/10</span>
                                                  </div>
                                                </div>
                                              </div>
                                            )}

                                            {/* תמונות */}
                                            {trade.images && trade.images.length > 0 && (
                                              <div className="details-section">
                                                <h5>📸 תמונות העסקה</h5>
                                                <div className="images-grid">
                                                  {trade.images.map((imageUrl, imgIndex) => (
                                                    <img
                                                      key={imgIndex}
                                                      src={imageUrl}
                                                      alt={`Trade screenshot ${imgIndex + 1}`}
                                                      className="trade-image"
                                                      onClick={() => window.open(imageUrl, '_blank')}
                                                    />
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="no-data">
                                <p>אין עסקאות ביומן זה</p>
                              </div>
                            )}
                          </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
                  <div className="no-data">
                    <h3>אין יומני מסחר</h3>
                    <p>משתמש זה עדיין לא יצר יומני מסחר</p>
                  </div>
                )}
              </div>
            )}

            {/* טאב תרגולים */}
            {/* מודל הצגת הגשה */}
            {showSubmissionModal && selectedSubmission && (
              <div className="modal-overlay" onClick={() => setShowSubmissionModal(false)}>
                <div className="modal-content submission-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>📝 {selectedSubmission.taskTitle}</h3>
                    <button className="close-modal-btn" onClick={() => setShowSubmissionModal(false)}>×</button>
                  </div>

                  <div className="modal-body">
                    {/* סטטוס */}
                    <div className="submission-status-section">
                      <span className={`status-badge large ${selectedSubmission.status}`}>
                        {selectedSubmission.status === 'accepted' ? '✅ אושר' :
                         selectedSubmission.status === 'rejected' ? '❌ נדחה' :
                         selectedSubmission.status === 'reviewed' ? '👁️ נבדק' :
                         '⏳ בבדיקה'}
                      </span>
                    </div>

                    {/* פרטים */}
                    <div className="submission-details">
                      <div className="detail-row">
                        <label>תאריך הגשה:</label>
                        <span>{new Date(selectedSubmission.submittedAt).toLocaleString('he-IL')}</span>
                      </div>
                      <div className="detail-row">
                        <label>סמל:</label>
                        <span>{selectedSubmission.symbol}</span>
                      </div>
                      <div className="detail-row">
                        <label>טיימפריים:</label>
                        <span>{selectedSubmission.interval}</span>
                      </div>
                    </div>

                    {/* הערות התלמיד */}
                    <div className="submission-notes">
                      <h5>הערות התלמיד:</h5>
                      <p>{selectedSubmission.notes}</p>
                    </div>

                    {/* תמונות */}
                    {selectedSubmission.screenshots && selectedSubmission.screenshots.length > 0 && (
                      <div className="submission-screenshots">
                        <h5>צילומי מסך:</h5>
                        <div className="screenshots-grid">
                          {selectedSubmission.screenshots.map((screenshot, index) => (
                            <div key={index} className="screenshot-item">
                              <img
                                src={screenshot.url}
                                alt={`Screenshot ${index + 1}`}
                                onClick={() => window.open(screenshot.url, '_blank')}
                              />
                              {screenshot.type && <span className="screenshot-type">{screenshot.type}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* משוב מנטור */}
                    {selectedSubmission.mentorFeedback && (
                      <div className="mentor-feedback">
                        <h5>💬 משוב מהמנטור:</h5>
                        <p>{selectedSubmission.mentorFeedback}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="no-data">
            <svg viewBox="0 0 24 24" width="64" height="64">
              <path fill="#8696a0" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <h3>משתמש לא נמצא במערכת</h3>
            <p>האם ברצונך לשלוח הזמנה למשתמש זה?</p>
            <button className="primary-btn" onClick={() => setShowInvitationModal(true)}>
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              שלח הזמנה
            </button>
          </div>
        )}
      </div>

      {/* מודל גישה לפלטפורמה */}
      {showAccessModal && (
        <div className="modal-overlay" onClick={() => setShowAccessModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>שליחת גישה לפלטפורמה</h3>
              <button className="close-modal-btn" onClick={() => setShowAccessModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>משתמש: <strong>{userData?.fullName}</strong></p>
              <p>אימייל: <strong>{userData?.email}</strong></p>
              <p className="info-message">קישור כניסה לפלטפורמה יישלח למשתמש בצ'אט WhatsApp</p>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowAccessModal(false)}>
                ביטול
              </button>
              <button
                className="save-btn"
                onClick={handleGrantAccess}
                disabled={grantingAccess}
              >
                {grantingAccess ? 'שולח...' : 'שלח קישור גישה'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* מודל שליחת הזמנה */}
      {showInvitationModal && (
        <div className="modal-overlay" onClick={() => setShowInvitationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>שליחת הזמנה להרשמה</h3>
              <button className="close-modal-btn" onClick={() => setShowInvitationModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>שם פרטי *</label>
                <input
                  type="text"
                  value={invitationData.firstName}
                  onChange={(e) => setInvitationData({...invitationData, firstName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>שם משפחה *</label>
                <input
                  type="text"
                  value={invitationData.lastName}
                  onChange={(e) => setInvitationData({...invitationData, lastName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>אימייל</label>
                <input
                  type="email"
                  value={invitationData.email}
                  onChange={(e) => setInvitationData({...invitationData, email: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>טלפון</label>
                <input
                  type="tel"
                  value={invitationData.phone}
                  onChange={(e) => setInvitationData({...invitationData, phone: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>סוג הכשרה *</label>
                <select
                  value={invitationData.courseType}
                  onChange={(e) => setInvitationData({...invitationData, courseType: e.target.value})}
                >
                  <option value="combined">המשולבת</option>
                  <option value="premium">פרימיום</option>
                  <option value="explosive_coins">מטבעות מתפוצצים</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowInvitationModal(false)}>
                ביטול
              </button>
              <button className="save-btn" onClick={handleSendInvitation}>
                שלח הזמנה
              </button>
            </div>
          </div>
        </div>
      )}

      {/* מודל אישור שינוי שלב התקדמות */}
      {showStageConfirmModal && pendingStageChange && (
        <div className="modal-overlay" onClick={cancelStageChange}>
          <div className="modal-content stage-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ אישור שינוי שלב</h3>
              <button className="close-modal-btn" onClick={cancelStageChange}>×</button>
            </div>
            <div className="modal-body">
              <div className="stage-confirm-content">
                <div className="stage-user-info">
                  <div className="user-avatar-confirm">
                    {selectedUser.profilePic ? (
                      <img src={selectedUser.profilePic} alt={userData?.fullName || selectedUser.name} />
                    ) : (
                      <div className="avatar-placeholder-confirm">
                        {(userData?.fullName || selectedUser.name)?.charAt(0) || 'U'}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="confirm-question">האם אתה בטוח שאתה רוצה לשנות את שלב ההתקדמות של</p>
                    <p className="confirm-user-name">{userData?.fullName || selectedUser.name}?</p>
                  </div>
                </div>

                <div className="stage-change-info">
                  <div className="stage-box current-stage">
                    <span className="stage-label">שלב נוכחי</span>
                    <div className="stage-display">
                      <span className="stage-icon">{progressStages.find(s => s.id === progressStage)?.icon}</span>
                      <span className="stage-name">{progressStages.find(s => s.id === progressStage)?.label}</span>
                    </div>
                  </div>

                  <div className="stage-arrow">→</div>

                  <div className="stage-box new-stage">
                    <span className="stage-label">שלב חדש</span>
                    <div className="stage-display">
                      <span className="stage-icon">{progressStages.find(s => s.id === pendingStageChange)?.icon}</span>
                      <span className="stage-name">{progressStages.find(s => s.id === pendingStageChange)?.label}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={cancelStageChange}>
                ביטול
              </button>
              <button className="save-btn confirm-stage-btn" onClick={confirmStageChange}>
                ✓ אשר שינוי
              </button>
            </div>
          </div>
        </div>
      )}

      {/* מודל נעילת/פתיחת שלב */}
      {showPhaseLockModal && pendingPhaseLock && (
        <div className="modal-overlay" onClick={cancelPhaseLockToggle}>
          <div className="modal-content phase-lock-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {pendingPhaseLock.action === 'unlock' ? '🔓 פתיחת שלב' : '🔒 נעילת שלב'}
              </h3>
              <button className="close-modal-btn" onClick={cancelPhaseLockToggle}>×</button>
            </div>

            <div className="modal-body">
              {/* כרטיס השלב */}
              <div className="phase-lock-info">
                <div className="phase-lock-icon" style={{ backgroundColor: pendingPhaseLock.phaseData.color }}>
                  {pendingPhaseLock.phaseData.icon}
                </div>
                <div className="phase-lock-details">
                  <h4>שלב {pendingPhaseLock.phaseId}: {pendingPhaseLock.phaseData.name}</h4>
                  <p className="phase-lock-description">
                    {pendingPhaseLock.action === 'unlock'
                      ? `האם אתה בטוח שברצונך לפתוח את "${pendingPhaseLock.phaseData.name}" עבור ${userData?.fullName}?`
                      : `האם אתה בטוח שברצונך לנעול את "${pendingPhaseLock.phaseData.name}" עבור ${userData?.fullName}?`
                    }
                  </p>
                </div>
              </div>

              {/* מידע נוסף */}
              <div className="phase-lock-extra-info">
                <div className="info-row">
                  <span className="info-label">👤 תלמיד:</span>
                  <span className="info-value">{userData?.fullName}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">📧 אימייל:</span>
                  <span className="info-value">{userData?.email}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">📊 הגשות בשלב:</span>
                  <span className="info-value">
                    {userSubmissions.filter(s => s.phase === pendingPhaseLock.phaseId).length} הגשות
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">🎯 סטטוס נוכחי:</span>
                  <span className="info-value">
                    {pendingPhaseLock.currentlyUnlocked
                      ? <span className="status-unlocked">🔓 פתוח</span>
                      : <span className="status-locked">🔒 נעול</span>
                    }
                  </span>
                </div>
              </div>

              {/* הודעת אזהרה */}
              {pendingPhaseLock.action === 'lock' && (
                <div className="phase-lock-warning">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                  </svg>
                  <span>שימו לב: נעילת השלב תמנע מהתלמיד גישה לתרגולים בשלב זה</span>
                </div>
              )}

              {pendingPhaseLock.action === 'unlock' && (
                <div className="phase-lock-success-msg">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  <span>התלמיד יקבל גישה מלאה לכל התרגולים והמשימות בשלב זה</span>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="cancel-btn" onClick={cancelPhaseLockToggle}>
                ביטול
              </button>
              <button
                className={`save-btn ${pendingPhaseLock.action === 'unlock' ? 'unlock-btn' : 'lock-btn'}`}
                onClick={confirmPhaseLockToggle}
              >
                {pendingPhaseLock.action === 'unlock' ? '🔓 פתח שלב' : '🔒 נעל שלב'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetailsPanel;
